import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "aip_chat_session";
const HISTORY_KEY = "aip_chat_history";

const SUGGESTED = [
  "What is the AiPresidential trilogy about?",
  "Where can I buy COGNITO?",
  "Who is Cognito?",
  "When does Book One launch?",
];

/**
 * ChatWidget — floating chat bubble in the bottom-right corner.
 * Opens a panel where visitors talk to "The Archivist". Conversation history is
 * kept client-side in localStorage so a refresh doesn't lose the thread; the
 * server-side session keeps the LLM's memory across requests.
 */
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; }
  });
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    try { sessionRef.current = localStorage.getItem(SESSION_KEY) || null; } catch { /* private mode */ }
  }, []);

  // Allow other components (e.g., the Blog page) to open the chat with an optional
  // pre-filled prompt by dispatching a CustomEvent: window.dispatchEvent(new CustomEvent('aip:open-chat', { detail: { prompt } }))
  useEffect(() => {
    const handler = (e) => {
      const prompt = e?.detail?.prompt || "";
      setOpen(true);
      if (prompt) setInput(prompt);
    };
    window.addEventListener("aip:open-chat", handler);
    return () => window.removeEventListener("aip:open-chat", handler);
  }, []);

  // Persist history
  useEffect(() => {
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-30))); } catch { /* ignore */ }
  }, [messages]);

  // Auto-scroll to latest
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg, at: Date.now() }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/chat`, {
        message: msg,
        session_id: sessionRef.current,
      });
      sessionRef.current = r.data.session_id;
      try { localStorage.setItem(SESSION_KEY, r.data.session_id); } catch { /* ignore */ }
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply, at: Date.now() }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "I'm having trouble responding right now. Please try again, or email AiPresidential2026@gmail.com.", at: Date.now(), error: true }]);
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); send(); };

  const resetChat = () => {
    setMessages([]);
    sessionRef.current = null;
    try {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(HISTORY_KEY);
    } catch { /* ignore */ }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[90] w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 to-purple-600 text-white shadow-[0_10px_40px_rgba(34,211,238,0.45)] flex items-center justify-center hover:scale-110 transition-transform"
          aria-label="Ask The Archivist"
          data-testid="chat-open-btn"
        >
          <MessageSquare className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-cyan-300 animate-ping" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-cyan-300" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[95] w-[min(92vw,400px)] h-[min(85vh,640px)] glass-strong rounded-3xl border border-cyan-400/40 shadow-2xl flex flex-col overflow-hidden"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-black/60 backdrop-blur">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-orbitron text-sm text-white truncate">The Archivist</p>
                <p className="text-[10px] uppercase tracking-widest text-cyan-300">AiPresidential assistant</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-gray-300 hover:text-white"
              aria-label="Close chat"
              data-testid="chat-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm text-gray-200">
                  Welcome. I'm The Archivist — I keep what's known about the AiPresidential trilogy. Ask me anything about the books, the world, or Cognito.
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-widest font-orbitron mt-4">Try asking…</p>
                <div className="flex flex-col gap-2">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-left text-sm px-4 py-2.5 rounded-2xl border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-400/5 text-gray-300 transition-colors"
                      data-testid={`chat-suggestion-${s.slice(0, 20).replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={
                  m.role === "user"
                    ? "max-w-[85%] bg-gradient-to-br from-cyan-400/20 to-purple-500/20 border border-cyan-400/30 rounded-2xl rounded-tr-md px-4 py-2.5 text-sm text-white"
                    : `max-w-[85%] bg-white/5 border ${m.error ? "border-rose-400/40" : "border-white/10"} rounded-2xl rounded-tl-md px-4 py-2.5 text-sm text-gray-200 whitespace-pre-wrap`
                }>
                  {m.text}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-md px-4 py-3 text-sm text-gray-400 inline-flex gap-1.5">
                  <span className="w-1.5 h-1.5 bg-cyan-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-cyan-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-cyan-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={onSubmit} className="border-t border-white/10 p-3 bg-black/40 flex gap-2 items-center">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about the trilogy…"
              maxLength={1000}
              className="flex-1 px-4 py-2.5 rounded-full bg-black/60 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-cyan-400/60"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 text-white flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
              data-testid="chat-send-btn"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

          {messages.length > 0 && (
            <button
              onClick={resetChat}
              className="absolute top-3 right-12 text-[10px] uppercase tracking-widest text-gray-500 hover:text-gray-300"
              data-testid="chat-reset-btn"
            >
              Reset
            </button>
          )}
        </div>
      )}
    </>
  );
}
